import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import ServiceAuthClient from './ServiceAuthClient';
import ServiceAuthContext from './ServiceAuthContext';
import { initialAuthState } from './initialAuthState';
import { hasAuthParams } from './ServiceAuthUtils';

const SS_SA_CODE_VERIFIER = 'pat.serviceauth.codeVerifier';
const SS_SA_STATE = 'pat.serviceauth.state';
const SS_RETURN = 'pat.serviceauth.return';

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISED':
      return {
        ...state,
        isAuthenticated: !!action.user,
        user: action.user,
        error: undefined,
        isLoading: false,
      };
    case 'HANDLE_REDIRECT_COMPLETE':
    case 'GET_ACCESS_TOKEN_COMPLETE':
      return {
        ...state,
        isAuthenticated: !!action.user,
        user: action.user,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: undefined,
      };
    case 'ERROR':
    default:
      return {
        ...state,
        error: action.error,
        isLoading: false,
      };
  }
};

const ServiceAuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [client] = useState(() => new ServiceAuthClient());
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const initializingRef = useRef(false);

  useEffect(() => {
    // Prevent multiple simultaneous initialization attempts
    if (initializingRef.current) {
      console.log(
        '[ServiceAuthProvider] Initialization already in progress, skipping...'
      );
      return;
    }

    initializingRef.current = true;

    (async () => {
      console.log('[ServiceAuthProvider] Initializing auth...');
      try {
        if (hasAuthParams()) {
          console.log(
            '[ServiceAuthProvider] Found auth params in URL - handling redirect callback'
          );
          const { returnTo } = await client.handleRedirectCallback();

          console.log(
            '[ServiceAuthProvider] Redirect callback complete, returning to:',
            returnTo
          );

          // Clear SS_RETURN now that we've successfully used it
          localStorage.removeItem(SS_RETURN);

          // Use replace: true to remove OAuth params from history
          // This prevents ?code=...&state=... from staying in the URL
          navigate(returnTo, { replace: true });
        } else {
          console.log(
            '[ServiceAuthProvider] No auth params - checking existing session'
          );

          // Safety check: Clear stale redirect flags to prevent infinite loops
          const hasToken = localStorage.getItem('pat.jwt.token');
          const redirectInProgress = localStorage.getItem(
            'pat.serviceauth.redirecting'
          );
          const redirectTimestamp = localStorage.getItem(
            'pat.serviceauth.redirect.timestamp'
          );

          if (redirectInProgress === 'true') {
            // Check if flag is stale (no token and flag is old)
            const flagAge = redirectTimestamp
              ? Date.now() - parseInt(redirectTimestamp, 10)
              : Infinity;
            const STALE_THRESHOLD = 30000; // 30 seconds

            if (!hasToken || flagAge > STALE_THRESHOLD) {
              console.warn(
                '[ServiceAuthProvider] Clearing stale redirect flag',
                `(no token: ${!hasToken}, age: ${Math.round(flagAge / 1000)}s)`
              );
              localStorage.removeItem('pat.serviceauth.redirecting');
              localStorage.removeItem('pat.serviceauth.redirect.timestamp');
            }
          }

          await client.checkSession();
        }
        const user = await client.getUser();
        console.log(
          '[ServiceAuthProvider] User retrieved:',
          user ? 'authenticated' : 'not authenticated'
        );
        // @ts-ignore
        localStorage.removeItem(SS_SA_STATE);
        localStorage.removeItem(SS_SA_CODE_VERIFIER);
        // Don't remove SS_RETURN here - it might still be needed if tokens are expired
        // It will be cleared after successful OAuth redirect callback

        // Clear refresh failed flag on successful init
        sessionStorage.removeItem('pat.refresh.failed');

        dispatch({ type: 'INITIALISED', user });
      } catch (error) {
        console.error(
          '[ServiceAuthProvider] Error during initialization:',
          error
        );

        // Clear redirect flag on error
        localStorage.removeItem('pat.serviceauth.redirecting');

        // If we got a state mismatch error, DO NOT trigger another redirect
        // This happens when the first auth attempt fails and we're already in callback flow
        if (
          error.message &&
          error.message.includes('State values do not match')
        ) {
          console.log(
            '[ServiceAuthProvider] State mismatch detected - clearing auth state and retrying'
          );

          // Get the saved return path BEFORE clearing anything
          const savedReturnPath = localStorage.getItem(SS_RETURN) || '/';

          // Clear all OAuth state to prevent loops
          localStorage.removeItem(SS_SA_STATE);
          localStorage.removeItem(SS_SA_CODE_VERIFIER);
          // Don't remove SS_RETURN - we need it for the retry!
          localStorage.removeItem('pat.serviceauth.redirecting'); // Clear redirect flag so new redirect can happen
          sessionStorage.removeItem('pat.refresh.failed');

          // Remove OAuth params from URL but preserve the original path we were trying to reach
          const cleanUrl = window.location.origin + savedReturnPath;
          console.log('[ServiceAuthProvider] Restoring URL to:', cleanUrl);
          window.history.replaceState({}, document.title, cleanUrl);

          // Dispatch error state so isAuthenticated = false
          dispatch({ type: 'ERROR', error });

          // Now ServiceAuthRoute will trigger a fresh redirect with the correct path
          return;
        }

        // @ts-ignore
        dispatch({ type: 'ERROR', error: error });
      } finally {
        // Reset initialization flag after completion or error
        initializingRef.current = false;
      }
    })();
  }, [navigate, client]);

  const redirectToServiceauth = useCallback(
    (opts) => client.redirectToServiceauth(),
    [client]
  );

  const logout = useCallback(
    (opts) => {
      client.logout();

      navigate(
        `/login?severity=success&message=${encodeURIComponent(
          'You have been signed out.'
        )}`
      );

      // @ts-ignore
      dispatch({ type: 'LOGOUT' });
    },
    [navigate, client]
  );

  const getAccessToken = useCallback(
    async (opts) => {
      let token;

      try {
        token = await client.getAccessToken(opts);
      } catch (error) {
        console.log('Error when getting token: ' + error);

        // If refresh token expired, handle gracefully
        if (error.message === 'REFRESH_TOKEN_EXPIRED') {
          console.log(
            '[ServiceAuthProvider] Refresh token expired - authentication required'
          );

          // Check if we've already detected this in this session to prevent infinite retries
          const refreshFailedFlag =
            sessionStorage.getItem('pat.refresh.failed');

          if (!refreshFailedFlag) {
            // First time detecting failed refresh - set flag and return null
            // This gives the component a chance to save data before redirect
            console.log(
              '[ServiceAuthProvider] First refresh failure - allowing component to handle'
            );
            sessionStorage.setItem('pat.refresh.failed', 'true');

            // Return null so component can handle it (save data, show message, etc.)
            return null;
          } else {
            // We've already tried once and user is still attempting actions
            // Now we should redirect to prevent infinite loop
            console.log(
              '[ServiceAuthProvider] Repeated action after refresh failure - forcing redirect'
            );

            // Clear all auth and session data to prevent retry loops
            client.clearAllAuthData();

            // Reset initialization ref
            if (initializingRef) {
              initializingRef.current = false;
            }

            // Set flag to indicate restoration should occur after redirect
            sessionStorage.setItem('template-form-should-restore', 'true');

            // Redirect to login (but not if we're already processing an OAuth callback)
            const hasOAuthParams =
              window.location.search.includes('code=') &&
              window.location.search.includes('state=');
            if (!hasOAuthParams) {
              console.log(
                '[ServiceAuthProvider] Redirecting to login due to expired refresh token'
              );
              client.redirectToServiceauth();
            } else {
              console.log(
                '[ServiceAuthProvider] OAuth callback in progress - not triggering another redirect'
              );
            }

            return null;
          }
        }

        // For other errors, return null and let component handle it
        return null;
      } finally {
        // @ts-ignore
        dispatch({
          type: 'GET_ACCESS_TOKEN_COMPLETE',
          user: await client.getUser(),
        });
      }

      return token;
    },
    [client, initializingRef]
  );

  const saveFormDataToSession = useCallback(
    (data, key) => client.saveFormDataToSession(data, key),
    [client]
  );

  const getFormDataFromSession = useCallback(
    (key) => client.getFormDataFromSession(key),
    [client]
  );

  return (
    <ServiceAuthContext.Provider
      value={{
        ...state,
        redirectToServiceauth,
        getAccessToken,
        logout,
        saveFormDataToSession,
        getFormDataFromSession,
      }}
    >
      {children}
    </ServiceAuthContext.Provider>
  );
};

export default ServiceAuthProvider;
